import type { MessageKey } from '../../i18n';

/**
 * ┌──────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT THE AGENT SAYS BEFORE ANYBODY HAS SAID ANYTHING.                │
 * └──────────────────────────────────────────────────────────────────┘
 *
 * One line, four bands of the day, and the reader's own given name in every one of them. It read
 * *"Jó napot. Min dolgozzunk?"* at two in the morning until 20 Sep, which is the kind of detail
 * that tells somebody nobody is home.
 *
 * ⚠️⚠️ **THE LINES LIVE IN `docs/plan/forecastify-koszontesek.md` AND THIS FILE FOLLOWS IT.**
 * Thirty-four of them, in both languages, chosen by the product owner. The catalogue holds the
 * strings; the only thing decided here is which band an hour is in and how one is picked.
 */

/**
 * ┌──────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ FOUR BANDS, AND THEY REPLACED SIX ON 21 SEP 2026.                        │
 * └──────────────────────────────────────────────────────────────────┘
 *
 * | band        | hours       |
 * | ----------- | ----------- |
 * | `morning`   | 04:01–12:00 |
 * | `afternoon` | 12:01–17:00 |
 * | `evening`   | 17:01–21:00 |
 * | `night`     | 21:01–04:00 |
 *
 * ⚠️⚠️ **THE SIX-PERIOD SET AND ITS LINES ARE GONE, AND SO IS THE RULE THEY CARRIED.** The note
 * above this one argued at length that a greeting must never open on a bare time-of-day statement
 * — no *Dél van*, no *Késő van* — and that each line should lean on something the product does.
 * The product owner read the result back on 21 Sep and called it cringe. These are the
 * replacements, and they are plain salutations on purpose: `docs/plan/forecastify-koszontesek.md`
 * is the source of truth and holds all thirty-four in both languages.
 *
 * ⚠️ **The boundaries are stated to the minute and implemented on the HOUR**, which differs
 * only at 17:00 — written as afternoon, read here as evening. A minute-accurate version would
 * take the clock rather than the hour, for a distinction nobody can perceive.
 */
export type GreetingPeriod = 'morning' | 'afternoon' | 'evening' | 'night';

/**
 * Which band an hour falls in.
 *
 * ⚠️⚠️ **`night` IS THE FALLBACK BECAUSE IT WRAPS MIDNIGHT.** 21:00–03:59 is the one window with
 * no single pair of neighbours on a 24-hour line; written as a range it needs a wrap and two
 * comparisons, written as the default it needs neither. Every hour that is not morning-or-later is
 * inside it, which includes 00:00–03:59.
 */
export function greetingPeriod(hour: number): GreetingPeriod {
  if (hour >= 21) return 'night';
  if (hour >= 17) return 'evening';
  if (hour >= 12) return 'afternoon';
  if (hour >= 4) return 'morning';
  /* ⚠️ 00:00–03:59 belongs with the hours BEFORE it: at half past two somebody is still
     finishing tonight rather than starting tomorrow. */
  return 'night';
}

/**
 * ⚠️ **Seven, nine, nine and FOUR** — the counts the source document carries, not a round
 * number chosen here. The catalogue holds each line in both languages.
 *
 * ⚠️ **Night lost five on 22 Sep 2026**, on the product owner's read-through: the ones that
 * commented on the hour rather than greeting somebody in it — *Késő van már*, *Még ébren?*,
 * *Csendes óra ez*, *Ne maradj fenn túl sokáig*, *Jó munkát ilyenkor is*. What is left greets and
 * then gets out of the way.
 */
const LINES: Readonly<Record<GreetingPeriod, readonly MessageKey[]>> = {
  morning: [
    'agent.hello.morning1',
    'agent.hello.morning2',
    'agent.hello.morning3',
    'agent.hello.morning4',
    'agent.hello.morning5',
    'agent.hello.morning6',
    'agent.hello.morning7',
  ],
  afternoon: [
    'agent.hello.afternoon1',
    'agent.hello.afternoon2',
    'agent.hello.afternoon3',
    'agent.hello.afternoon4',
    'agent.hello.afternoon5',
    'agent.hello.afternoon6',
    'agent.hello.afternoon7',
    'agent.hello.afternoon8',
    'agent.hello.afternoon9',
  ],
  evening: [
    'agent.hello.evening1',
    'agent.hello.evening2',
    'agent.hello.evening3',
    'agent.hello.evening4',
    'agent.hello.evening5',
    'agent.hello.evening6',
    'agent.hello.evening7',
    'agent.hello.evening8',
    'agent.hello.evening9',
  ],
  night: ['agent.hello.night1', 'agent.hello.night2', 'agent.hello.night3', 'agent.hello.night4'],
};

/*
 * ⚠️⚠️ **THERE WAS A `givenNameOf` HERE AND IT WAS A GUESS.** It took the LAST word of the
 * composed `display_name`, on the reasoning that Hungarian writes the family name first — correct
 * for *"Takács Gábor"*, wrong for *"Adrian Petraki"*, and undecidable from the string either way.
 *
 * The product owner's answer on 20 Sep was the obvious one: the halves are already stored, and the
 * Account screen already edits them. `0148` carries `given_name` on `GET /auth/me`, the session
 * holds it, and the screen reads it. Nothing splits a name any more.
 */

/**
 * The key to render, given the hour and a number to choose with.
 *
 * ⚠️⚠️ **THE CALLER SUPPLIES THE RANDOMNESS, WHICH IS WHY THIS IS TESTABLE AT ALL.** A `Math.random`
 * in here would make every case about this function a coin toss — and worse, it would re-roll on
 * every render, so the greeting would change as somebody typed. The screen picks a number when a
 * new chat starts and holds it; this only turns that number into a line.
 *
 * ⚠️ `pick` is taken modulo the period's length, so a caller passing anything in `[0, 1)` scaled
 * however it likes still lands on a real line.
 */
export function greetingKey(hour: number, pick: number): MessageKey {
  const lines = LINES[greetingPeriod(hour)];
  const index = Math.abs(Math.trunc(pick)) % lines.length;
  /* ⚠️ `?? lines[0]` for the type only: `index` is already inside the array by construction, and
     the non-null assertion this replaces is the thing `eslint` is right to refuse. */
  return lines[index] ?? lines[0] ?? 'agent.greeting';
}
